import { useState, useEffect, useCallback, useMemo } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { ArrowLeft, Calendar, Banknote, Building, FileText, Send, Award, XCircle, MessageSquare, History, Plus, Users, Trophy } from 'lucide-react';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { toast } from 'sonner';
import { rfqAPI, suppliersAPI, fetchAllPages } from '../lib/api';
import type { RFQ, RFQQuery, SupplierResponseListItem, RFQActivityLog, SupplierResponse, SupplierProfile } from '../lib/api';
import { getApiErrorMessage } from '../lib/api/types/common';
import type { SupplierOption } from '../components/rfq/rfqWizardTypes';
import { useFormatters } from '../contexts/PreferencesContext';
import { useAuth } from '../contexts/AuthContext';
import { useIsSupplier } from '../hooks/usePortalMode';
import ResponseComparison from '../components/rfq/ResponseComparison';
import QueryThread from '../components/rfq/QueryThread';
import SupplierResponseForm from '../components/rfq/SupplierResponseForm';
import RFQDetailInfoTabs from '../components/rfq/RFQDetailInfoTabs';
import RFQActivityTab from '../components/rfq/RFQActivityTab';
import { SendToSuppliersDialog, CancelRFQDialog, CreateQueryDialog } from '../components/rfq/RFQDetailDialogs';
import { filterActivityForSupplier } from '../components/rfq/rfqActivity';
import { useRfqMoney } from '../components/rfq/useRfqMoney';
import { isRfqClosed } from '../components/rfq/rfqResponseState';

const STATUS_COLORS: Record<string, string> = {
  draft: 'bg-gray-100 text-gray-800',
  sent: 'bg-blue-100 text-blue-800',
  submitted: 'bg-yellow-100 text-yellow-800',
  awarded: 'bg-green-100 text-green-800',
  cancelled: 'bg-gray-300 text-gray-700',
};
const PRIORITY_COLORS: Record<string, string> = {
  low: 'bg-gray-100 text-gray-600',
  medium: 'bg-blue-100 text-blue-600',
  high: 'bg-orange-100 text-orange-600',
  urgent: 'bg-red-100 text-red-600',
};

function unwrapList<T>(data: { results?: T[] } | T[] | undefined): T[] {
  if (!data) return [];
  return Array.isArray(data) ? data : data.results ?? [];
}

export default function RFQDetail() {
  const { formatDate, formatDateTime } = useFormatters();
  const { user } = useAuth();
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const rfqId = Number(id);
  const [rfq, setRfq] = useState<RFQ | null>(null);
  const [loading, setLoading] = useState(true);
  const [actionLoading, setActionLoading] = useState(false);
  const [showSendDialog, setShowSendDialog] = useState(false);
  // Suppliers the send dialog can invite — loaded on first open, same
  // source and shape as the create wizard's Suppliers step.
  const [supplierOptions, setSupplierOptions] = useState<SupplierOption[]>([]);
  const [supplierOptionsLoading, setSupplierOptionsLoading] = useState(false);
  const [showCancelDialog, setShowCancelDialog] = useState(false);
  const [cancelReason, setCancelReason] = useState('');

  const [queries, setQueries] = useState<RFQQuery[]>([]);
  const [responses, setResponses] = useState<SupplierResponseListItem[]>([]);
  const [activityLogs, setActivityLogs] = useState<RFQActivityLog[]>([]);
  const [showCreateQueryDialog, setShowCreateQueryDialog] = useState(false);
  const [newQuerySubject, setNewQuerySubject] = useState('');
  const [newQueryMessage, setNewQueryMessage] = useState('');
  const [newQuerySupplierId, setNewQuerySupplierId] = useState('');
  const [creatingQuery, setCreatingQuery] = useState(false);

  const [myResponse, setMyResponse] = useState<SupplierResponse | null>(null);

  const isSupplier = useIsSupplier();
  const userCompanyId = user?.company;
  const formatMoney = useRfqMoney(rfq?.currency);

  const fetchRFQDetail = useCallback(async () => {
    try {
      setLoading(true);
      const response = await rfqAPI.getRFQ(rfqId);
      setRfq(response.data);
    } catch (error) {
      console.error('Error fetching RFQ details:', error);
    } finally {
      setLoading(false);
    }
  }, [rfqId]);

  const fetchQueries = useCallback(async () => {
    try {
      const response = await rfqAPI.listQueries(rfqId);
      setQueries(unwrapList<RFQQuery>(response.data));
    } catch (error) {
      console.error('Error fetching queries:', error);
    }
  }, [rfqId]);

  const fetchResponses = useCallback(async () => {
    try {
      const response = await rfqAPI.listResponses(rfqId);
      setResponses(unwrapList<SupplierResponseListItem>(response.data));
    } catch (error) {
      console.error('Error fetching responses:', error);
    }
  }, [rfqId]);

  const fetchActivityLogs = useCallback(async () => {
    try {
      const response = await rfqAPI.getActivityLog(rfqId);
      setActivityLogs(unwrapList<RFQActivityLog>(response.data));
    } catch (error) {
      console.error('Error fetching activity logs:', error);
    }
  }, [rfqId]);

  const fetchMyResponse = useCallback(async (responseId: number) => {
    try {
      const response = await rfqAPI.getResponse(responseId);
      setMyResponse(response.data);
    } catch (error) {
      console.error('Error fetching my response:', error);
    }
  }, []);

  useEffect(() => {
    fetchRFQDetail();
    fetchQueries();
    fetchResponses();
    fetchActivityLogs();
  }, [fetchRFQDetail, fetchQueries, fetchResponses, fetchActivityLogs]);

  // Supplier: load their own response once the (supplier-scoped) list arrives.
  useEffect(() => {
    if (!isSupplier || !userCompanyId || responses.length === 0) return;
    const mine = responses.find(r => r.supplier === userCompanyId);
    if (mine) fetchMyResponse(mine.id);
  }, [isSupplier, userCompanyId, responses, fetchMyResponse]);

  // A supplier can only ever hold a thread in its own name, and the server
  // enforces that. A customer holds one thread per bidder, so it has to say
  // which — there is nothing to infer from.
  const querySupplierId = isSupplier ? userCompanyId : Number(newQuerySupplierId) || undefined;

  const handleCreateQuery = async () => {
    if (!newQuerySubject.trim() || !newQueryMessage.trim()) {
      toast.error('Please enter a subject and message');
      return;
    }
    if (!querySupplierId) {
      toast.error(isSupplier
        ? 'Your company could not be identified — reload and try again'
        : 'Choose which supplier this query is with');
      return;
    }
    try {
      setCreatingQuery(true);
      await rfqAPI.createQuery({
        rfq_id: rfqId,
        supplier_id: querySupplierId,
        subject: newQuerySubject.trim(),
        message: newQueryMessage.trim(),
      });
      toast.success('Query created successfully');
      setShowCreateQueryDialog(false);
      setNewQuerySubject('');
      setNewQueryMessage('');
      fetchQueries();
    } catch (error) {
      console.error('Error creating query:', error);
      toast.error('Failed to create query');
    } finally {
      setCreatingQuery(false);
    }
  };

  // Load once, the first time the dialog opens. Deliberately not an effect
  // keyed on the loading flag: that re-ran on the flag's own change and its
  // cleanup discarded the in-flight result, so the picker spun forever.
  const loadSupplierOptions = useCallback(async () => {
    if (supplierOptions.length > 0 || supplierOptionsLoading) return;
    setSupplierOptionsLoading(true);
    try {
      const list = await fetchAllPages<SupplierProfile>((page) =>
        suppliersAPI.getProfiles({ page_size: 200, minimal: true, is_active: true, page }),
      );
      setSupplierOptions(list.map((p) => ({ id: p.id, company: p.company ?? p.company_id, company_name: p.company_name })));
    } catch (error: unknown) {
      console.error('Error loading suppliers for send dialog:', error);
      toast.error('Could not load the supplier list. Close and try again.');
    } finally {
      setSupplierOptionsLoading(false);
    }
  }, [supplierOptions.length, supplierOptionsLoading]);

  useEffect(() => {
    if (showSendDialog) void loadSupplierOptions();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [showSendDialog]);

  // Suppliers already on the RFQ (existing responses) come pre-ticked.
  const alreadyInvited = useMemo(() => responses.map((r) => r.supplier).filter((id): id is number => typeof id === 'number'), [responses]);

  const handleSendToSuppliers = async (supplierIds: number[]) => {
    if (!rfq) return;
    try {
      setActionLoading(true);
      await rfqAPI.sendToSuppliers(rfq.id, { supplier_ids: supplierIds });
      const n = supplierIds.length;
      toast.success(rfq.status === 'draft'
        ? `RFQ sent to ${n} supplier${n === 1 ? '' : 's'}`
        : `Invited ${n} more supplier${n === 1 ? '' : 's'}`);
      setShowSendDialog(false);
      fetchRFQDetail();
    } catch (error) {
      console.error('Error sending RFQ:', error);
      // Surface the server's reason ("At least one supplier must be
      // selected", an NDA missing, …) instead of a generic failure.
      toast.error(getApiErrorMessage(error, 'Failed to send RFQ to suppliers'));
    } finally {
      setActionLoading(false);
    }
  };

  const handleCancel = async () => {
    if (!rfq) return;
    try {
      setActionLoading(true);
      await rfqAPI.cancelRFQ(rfq.id, { reason: cancelReason });
      toast.success('RFQ cancelled successfully');
      setShowCancelDialog(false);
      setCancelReason('');
      fetchRFQDetail();
    } catch (error) {
      console.error('Error cancelling RFQ:', error);
      toast.error('Failed to cancel RFQ');
    } finally {
      setActionLoading(false);
    }
  };

  // Supplier-side award outcome. Never depends on awarded_supplier_name —
  // the backend redacts it for non-winners.
  const isWinner = Boolean(rfq && rfq.status === 'awarded' && userCompanyId && rfq.awarded_supplier === userCompanyId);
  const visibleActivity = useMemo(() => {
    if (!isSupplier || !userCompanyId) return activityLogs;
    return filterActivityForSupplier(activityLogs, {
      companyId: userCompanyId,
      ownQueryIds: queries.map(q => q.id),
      isWinner,
    });
  }, [isSupplier, userCompanyId, activityLogs, queries, isWinner]);

  if (loading) return <div className="p-6 text-center">Loading...</div>;
  if (!rfq) return <div className="p-6 text-center">RFQ not found</div>;

  const isAwarded = rfq.status === 'awarded';
  const thirdCardLabel = isAwarded ? 'Awarded Supplier' : 'Customer';
  const thirdCardValue = isAwarded
    ? (isSupplier
        ? (isWinner ? 'Awarded to you' : 'Awarded to another supplier')
        : (rfq.awarded_supplier_name || 'Awarded'))
    : rfq.customer_name;

  return (
    <div className="p-6 space-y-6">
      <div className="flex items-center gap-4">
        <Button variant="ghost" size="icon" onClick={() => navigate('/rfq')}>
          <ArrowLeft className="h-5 w-5" />
        </Button>
        <div className="flex-1">
          <div className="flex items-center gap-2 mb-2">
            <h1 className="text-3xl font-bold">{rfq.rfq_number}</h1>
            <Badge className={STATUS_COLORS[rfq.status] || 'bg-gray-100'}>{rfq.status.toUpperCase()}</Badge>
            <Badge className={PRIORITY_COLORS[rfq.priority] || 'bg-gray-100'}>{rfq.priority.toUpperCase()}</Badge>
          </div>
          <p className="text-xl text-muted-foreground">{rfq.title}</p>
        </div>
        {/* Customer-only actions — suppliers never see send / award / cancel. */}
        {!isSupplier && (
          <div className="flex gap-2">
            {rfq.status === 'draft' && (
              <Button onClick={() => setShowSendDialog(true)} className="gap-2">
                <Send className="h-4 w-4" />
                Send to Suppliers
              </Button>
            )}
            {(rfq.status === 'sent' || rfq.status === 'submitted') && (
              <Button variant="outline" onClick={() => setShowSendDialog(true)} className="gap-2" title="Invite additional suppliers to this RFQ">
                <Users className="h-4 w-4" />
                Invite More Suppliers
              </Button>
            )}
            {(rfq.status === 'sent' || rfq.status === 'submitted') && (
              <Button variant="default" className="gap-2" disabled title="Award from the Responses tab">
                <Award className="h-4 w-4" />
                Award RFQ
              </Button>
            )}
            {rfq.status !== 'cancelled' && !isAwarded && (
              <Button variant="outline" onClick={() => setShowCancelDialog(true)} className="gap-2 text-destructive hover:text-destructive">
                <XCircle className="h-4 w-4" />
                Cancel
              </Button>
            )}
          </div>
        )}
      </div>

      {isSupplier && isAwarded && (
        <Card className={isWinner ? 'border-green-500 bg-green-50' : 'border-gray-300 bg-gray-50'}>
          <CardContent className="pt-4 flex items-center gap-2 text-sm">
            <Trophy className={`h-5 w-5 ${isWinner ? 'text-green-600' : 'text-gray-500'}`} />
            {/* Thanking a supplier for a response it never sent reads as a form
                letter. A bidder that did not quote is told the outcome without
                being credited with taking part. */}
            {isWinner
              ? 'Congratulations — this RFQ has been awarded to your company.'
              : myResponse?.response_status === 'no_bid'
                ? 'This RFQ has been awarded. Your company did not submit a quotation.'
                : 'This RFQ has been awarded to another supplier. Thank you for your response.'}
          </CardContent>
        </Card>
      )}

      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        <Card>
          <CardHeader className="pb-2">
            <CardDescription className="flex items-center gap-2"><Banknote className="h-4 w-4" />Total Value</CardDescription>
            <CardTitle className="text-2xl">{formatMoney(rfq.total_estimated_value)}</CardTitle>
          </CardHeader>
        </Card>
        <Card>
          <CardHeader className="pb-2">
            <CardDescription className="flex items-center gap-2"><Calendar className="h-4 w-4" />Response Deadline</CardDescription>
            <CardTitle className="text-lg">{formatDate(rfq.response_deadline)}</CardTitle>
          </CardHeader>
        </Card>
        <Card>
          <CardHeader className="pb-2">
            <CardDescription className="flex items-center gap-2"><Building className="h-4 w-4" />{thirdCardLabel}</CardDescription>
            <CardTitle className="text-lg">{thirdCardValue}</CardTitle>
          </CardHeader>
        </Card>
        <Card>
          <CardHeader className="pb-2">
            <CardDescription className="flex items-center gap-2"><FileText className="h-4 w-4" />Category</CardDescription>
            <CardTitle className="text-lg capitalize">{rfq.category}</CardTitle>
          </CardHeader>
        </Card>
      </div>

      <Tabs defaultValue="overview" className="w-full">
        <TabsList>
          <TabsTrigger value="overview">Overview</TabsTrigger>
          <TabsTrigger value="line-items">Line Items ({rfq.line_items?.length || 0})</TabsTrigger>
          <TabsTrigger value="responses" className="flex items-center gap-1">
            <Users className="h-3 w-3" />
            {isSupplier ? 'My Response' : `Responses (${responses.length})`}
          </TabsTrigger>
          <TabsTrigger value="queries" className="flex items-center gap-1">
            <MessageSquare className="h-3 w-3" />
            Queries ({queries.length})
          </TabsTrigger>
          <TabsTrigger value="certificates">Certificates ({rfq.required_certificates?.length || 0})</TabsTrigger>
          <TabsTrigger value="documents">Documents ({rfq.documents?.length || 0})</TabsTrigger>
          <TabsTrigger value="activity" className="flex items-center gap-1">
            <History className="h-3 w-3" />
            Activity
          </TabsTrigger>
        </TabsList>

        <RFQDetailInfoTabs
          rfq={rfq}
          formatMoney={formatMoney}
          formatDate={(d) => formatDate(d)}
          // Mirrors _CUSTOMER_WRITE on RFQDocumentViewSet, and stops once the
          // RFQ is closed — attaching a drawing to a decided enquiry only
          // confuses the record.
          canAttachDocuments={!isSupplier && !isRfqClosed(rfq)}
          onDocumentUploaded={fetchRFQDetail}
        />

        <TabsContent value="responses" className="space-y-4">
          {isSupplier ? (
            myResponse ? (
              <SupplierResponseForm
                rfq={rfq}
                response={myResponse}
                onSuccess={() => { fetchMyResponse(myResponse.id); fetchResponses(); }}
                onClose={() => navigate('/rfq')}
              />
            ) : (
              <Card>
                <CardContent className="text-center py-12">
                  <Users className="h-12 w-12 mx-auto text-muted-foreground mb-4" />
                  <p className="text-muted-foreground">
                    {responses.length > 0 ? 'Loading your response...' : 'You are not invited to respond to this RFQ.'}
                  </p>
                </CardContent>
              </Card>
            )
          ) : (
            <ResponseComparison rfq={rfq} responses={responses} onAward={() => { fetchRFQDetail(); fetchResponses(); }} />
          )}
        </TabsContent>

        <TabsContent value="queries" className="space-y-4">
          <div className="flex justify-between items-center">
            <h3 className="text-lg font-medium">Query Threads</h3>
            <Button onClick={() => setShowCreateQueryDialog(true)} size="sm">
              <Plus className="h-4 w-4 mr-2" />
              New Query
            </Button>
          </div>
          {queries.length > 0 ? (
            <div className="space-y-4">
              {queries.map((query) => (
                <QueryThread
                  key={query.id}
                  query={query}
                  currentUserId={user?.id || 0}
                  onRefresh={fetchQueries}
                  canAttach={!isSupplier}
                />
              ))}
            </div>
          ) : (
            <Card>
              <CardContent className="text-center py-12">
                <MessageSquare className="h-12 w-12 mx-auto text-muted-foreground mb-4" />
                <p className="text-muted-foreground">No queries yet.</p>
                <p className="text-sm text-muted-foreground mt-2">
                  {isSupplier ? 'Create a query to ask the customer about this RFQ.' : 'Create a query to communicate with suppliers about this RFQ.'}
                </p>
              </CardContent>
            </Card>
          )}
        </TabsContent>

        <TabsContent value="activity" className="space-y-4">
          <RFQActivityTab logs={visibleActivity} formatDateTime={(d) => formatDateTime(d)} />
        </TabsContent>
      </Tabs>

      {!isSupplier && (
        <>
          <SendToSuppliersDialog
            open={showSendDialog}
            busy={actionLoading}
            suppliers={supplierOptions}
            suppliersLoading={supplierOptionsLoading}
            initialSelected={alreadyInvited}
            mode={rfq.status === 'draft' ? 'send' : 'invite'}
            onOpenChange={setShowSendDialog}
            onConfirm={handleSendToSuppliers}
          />
          <CancelRFQDialog
            open={showCancelDialog}
            busy={actionLoading}
            reason={cancelReason}
            onReasonChange={setCancelReason}
            onOpenChange={setShowCancelDialog}
            onConfirm={handleCancel}
          />
        </>
      )}
      <CreateQueryDialog
        open={showCreateQueryDialog}
        busy={creatingQuery}
        subject={newQuerySubject}
        message={newQueryMessage}
        supplierId={newQuerySupplierId}
        // Empty for a supplier, which hides the picker: the thread is always in
        // their own name and the server refuses any other.
        supplierOptions={isSupplier
          ? []
          : responses.map(r => ({ id: r.supplier, name: r.supplier_name }))}
        onSupplierChange={setNewQuerySupplierId}
        onSubjectChange={setNewQuerySubject}
        onMessageChange={setNewQueryMessage}
        onOpenChange={(open) => {
          setShowCreateQueryDialog(open);
          if (!open) { setNewQuerySubject(''); setNewQueryMessage(''); setNewQuerySupplierId(''); }
        }}
        onConfirm={handleCreateQuery}
      />
    </div>
  );
}
